import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { productCategories as defaultProducts } from '../data/productCategories';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductShowcase = () => {
  const { isDark } = useTheme();
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);

  // Translation key mapping for product slugs
  const getProductTranslationKey = (slug) => {
    const keyMap = {
      'led-neon-signs': 'neonSigns',
      'led-signs': 'ledSigns',
      'channel-letter-signs': 'channelLetters',
      'acrylic-signs': 'acrylicSigns',
      'metal-signs': 'metalSigns',
      'lightbox-signs': 'lightboxSigns',
      'custom-signs': 'customSigns'
    };
    return keyMap[slug] || slug;
  };

  // Get translated product name and description
  const getTranslatedProduct = (product) => {
    const key = getProductTranslationKey(product.slug);
    const translatedName = t(`products.${key}`, { defaultValue: product.name });
    const translatedDesc = t(`products.${key}Desc`, { defaultValue: product.description });
    return {
      ...product,
      name: translatedName,
      description: translatedDesc
    };
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/products`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setProducts(data);
          } else {
            // Fallback to default products if database is empty
            setProducts(defaultProducts);
          }
        } else {
          setProducts(defaultProducts);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts(defaultProducts);
      }
    };
    fetchProducts();
  }, []);

  return (
    <section 
      className="py-20 transition-colors duration-300"
      style={{ backgroundColor: isDark ? '#0a0a0a' : '#f8fafc' }}
      id="products"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'gradient-text-dark' : 'gradient-text-light'}`}
            style={{
              filter: isDark 
                ? 'drop-shadow(0 0 15px rgba(255, 0, 255, 0.5))'
                : 'drop-shadow(0 0 10px rgba(255, 20, 147, 0.4))'
            }}
            data-testid="products-section-title"
          >
            {t('productShowcase.title')}
          </h2>
          <p 
            className="text-lg md:text-xl max-w-2xl mx-auto"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('productShowcase.subtitle')}
          </p>
        </div>

        {/* Product Grid - 2 columns on mobile, 2 on tablet, 3 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {products.filter(p => p.slug !== 'custom-signs').slice(0, 6).map((product, index) => {
            const translatedProduct = getTranslatedProduct(product);
            return (
            <Link
              key={product.id}
              to={`/products/${product.slug}`}
              className="group relative overflow-hidden rounded-xl md:rounded-2xl transition-all duration-500 hover:scale-[1.02]"
              style={{
                border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
                boxShadow: isDark 
                  ? '0 0 30px rgba(255, 0, 255, 0.2)' 
                  : '0 0 20px rgba(255, 20, 147, 0.15)'
              }}
              data-testid={`product-card-${product.slug}`}
            >
              {/* Image */}
              <div className="relative h-32 md:h-48 overflow-hidden">
                <img 
                  src={product.image || (product.gallery && product.gallery[0]?.image)} 
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div 
                  className="absolute inset-0 transition-opacity duration-300"
                  style={{
                    background: isDark 
                      ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                      : 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
                  }}
                />
                
                {/* Category badge - smaller on mobile */}
                {product.hasCustomizer && (
                  <div 
                    className="absolute top-2 right-2 md:top-3 md:right-3 px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-bold"
                    style={{
                      background: isDark 
                        ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                        : 'linear-gradient(135deg, #00bfff 0%, #10b981 100%)',
                      color: '#000000'
                    }}
                  >
                    {t('productShowcase.customizer', 'Customizer')}
                  </div>
                )}
              </div>

              {/* Content - smaller padding on mobile */}
              <div 
                className="p-3 md:p-6 transition-colors duration-300"
                style={{ 
                  backgroundColor: isDark ? 'rgba(10, 10, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)'
                }}
              >
                <h3 
                  className="text-sm md:text-xl font-bold mb-1 md:mb-2 transition-colors duration-300 line-clamp-1"
                  style={{
                    color: isDark ? '#ffffff' : '#0f172a'
                  }}
                >
                  {translatedProduct.name}
                </h3>
                <p 
                  className="text-xs md:text-sm mb-2 md:mb-4 line-clamp-2 hidden md:block"
                  style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                >
                  {translatedProduct.description}
                </p>
                
                <div className="flex items-center justify-end">
                  <span 
                    className="flex items-center gap-1 text-xs md:text-sm font-medium transition-all duration-300 group-hover:gap-2"
                    style={{
                      color: isDark ? '#00ffff' : '#00bfff',
                      textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
                    }}
                  >
                    {t('productShowcase.viewMore', 'View More')}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>

              {/* Hover glow effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  boxShadow: isDark 
                    ? 'inset 0 0 60px rgba(255, 0, 255, 0.3)' 
                    : 'inset 0 0 40px rgba(255, 20, 147, 0.2)'
                }}
              />
            </Link>
          );
          })}
        </div>

        {/* Centered Custom Sign Card */}
        {products.find(p => p.slug === 'custom-signs') && (
          <div className="flex justify-center mt-6">
            {(() => {
              const customProduct = products.find(p => p.slug === 'custom-signs');
              const translatedCustomProduct = getTranslatedProduct(customProduct);
              return (
                <Link
                  to={`/products/${customProduct.slug}`}
                  className="group relative overflow-hidden rounded-2xl transition-all duration-500 hover:scale-[1.02] w-full md:w-1/2 lg:w-1/3"
                  style={{
                    border: `2px solid ${isDark ? '#ff00ff' : '#ff1493'}`,
                    boxShadow: isDark 
                      ? '0 0 30px rgba(255, 0, 255, 0.2)' 
                      : '0 0 20px rgba(255, 20, 147, 0.15)'
                  }}
                  data-testid={`product-card-${customProduct.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={customProduct.image || (customProduct.gallery && customProduct.gallery[0]?.image)} 
                      alt={translatedCustomProduct.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div 
                      className="absolute inset-0 transition-opacity duration-300"
                      style={{
                        background: isDark 
                          ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                          : 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div 
                    className="p-5"
                    style={{ backgroundColor: isDark ? '#0a0a0a' : '#ffffff' }}
                  >
                    <h3 
                      className="text-xl font-bold mb-2"
                      style={{ 
                        color: isDark ? '#ffffff' : '#0f172a',
                      }}
                    >
                      {translatedCustomProduct.name}
                    </h3>
                    <p 
                      className="text-sm mb-4 line-clamp-2"
                      style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
                    >
                      {translatedCustomProduct.description}
                    </p>
                    
                    <div className="flex items-center justify-end">
                      <span 
                        className="flex items-center gap-1 text-sm font-medium transition-all duration-300 group-hover:gap-2"
                        style={{
                          color: isDark ? '#00ffff' : '#00bfff',
                          textShadow: isDark ? '0 0 10px rgba(0, 255, 255, 0.5)' : 'none'
                        }}
                      >
                        {t('productShowcase.viewMore', 'View More')}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>

                  {/* Hover glow effect */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      boxShadow: isDark 
                        ? 'inset 0 0 60px rgba(255, 0, 255, 0.3)' 
                        : 'inset 0 0 40px rgba(255, 20, 147, 0.2)'
                    }}
                  />
                </Link>
              );
            })()}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p 
            className="text-lg mb-4"
            style={{ color: isDark ? '#a0a0a0' : '#64748b' }}
          >
            {t('productShowcase.dontSee', "Don't see what you're looking for?")}
          </p>
          <Link
            to="/products/custom-signs"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105"
            style={{
              background: isDark 
                ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)'
                : 'linear-gradient(135deg, #00bfff 0%, #10b981 100%)',
              color: '#000000',
              boxShadow: isDark 
                ? '0 10px 40px rgba(0, 255, 255, 0.4)' 
                : '0 10px 30px rgba(0, 191, 255, 0.3)'
            }}
            data-testid="custom-sign-cta"
          >
            {t('productShowcase.requestCustom', 'Request a Custom Sign')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
